import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Container,
    Typography,
    Chip,
    Avatar,
    Divider,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Link as LinkMUI,
    Breadcrumbs,
    CircularProgress
} from '@mui/material';
import { useParams, Link } from 'react-router-dom';
import { CalendarToday as CalendarIcon, AccessTime as TimeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import { getAppTheme } from '../services/authContext';
import { useSelector } from 'react-redux';
import { blogApi } from '../services/mle-api';
import SEO from '../components/SEO/SEO';
import { getBlogSeoData } from '../utils/seoConfig';

const BlogDetail = () => {
    const mode = useSelector((state) => state.theme.mode);
    const theme = getAppTheme(mode);
    const { slug } = useParams(); // Get the slug from the URL

    const [blog, setBlog] = useState(null);
    const [relatedPosts, setRelatedPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchBlogData = useCallback(async () => {
        if (!slug) return;
        setLoading(true);
        setError(null);
        try {
            // Fetch the main blog post by its slug
            const blogResponse = await blogApi.list({ slug });
            if (blogResponse.data.results && blogResponse.data.results.length > 0) {
                const currentPost = blogResponse.data.results[0];
                setBlog(currentPost);

                // Fetch related posts from the same category
                const relatedResponse = await blogApi.list({ category__slug: currentPost.category_slug, page_size: 3 });
                // Filter out the current post from the related list
                setRelatedPosts(relatedResponse.data.results.filter(p => p.id !== currentPost.id).slice(0, 2));
            } else {
                setError("Blog post not found.");
            }
        } catch (err) {
            console.error("Failed to fetch blog data:", err);
            setError("Failed to load the blog post.");
        } finally {
            setLoading(false);
        }
    }, [slug]);

    useEffect(() => {
        fetchBlogData();
    }, [fetchBlogData]);

    // Function to calculate read time (approax)
    const calculateReadTime = (content) => {
        const wordsPerMinute = 200;
        const textLength = content.replace(/<[^>]+>/g, '').split(/\s+/).length;
        if (textLength > 0) {
            return Math.ceil(textLength / wordsPerMinute) + " min read";
        }
        return "1 min read";
    };

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="h5" color="text.secondary">{error}</Typography>
            </Box>
        );
    }

    const seoData = getBlogSeoData(blog);

    return (
        <ThemeProvider theme={theme}>
            <SEO {...seoData} type="article" />
            <Box sx={{ py: 8, minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
                <Container maxWidth="lg">
                    <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 4 }}>
                        <LinkMUI component={Link} to="/" underline="hover" color="inherit">Home</LinkMUI>
                        <LinkMUI component={Link} to="/blog" underline="hover" color="inherit">Blog</LinkMUI>
                        <Typography color="text.primary">{blog.title}</Typography>
                    </Breadcrumbs>

                    <Grid container spacing={5}>
                        {/* Main Content */}
                        <Grid item xs={12} md={8}>
                            <Box>
                                <Box sx={{ mb: 2 }}>
                                    <Chip label={blog.category_name} sx={{ mr: 1, backgroundColor: theme.palette.primary.main, color: '#fff' }} />
                                </Box>

                                <Typography variant="h3" component="h1" gutterBottom sx={{ color: theme.palette.heading.h2, fontWeight: 700 }}>
                                    {blog.title}
                                </Typography>

                                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Avatar alt={blog.author_name} />
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                                                {blog.author_name}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.text.secondary }}>
                                        <CalendarIcon fontSize="small" />
                                        <Typography variant="body2">{new Date(blog.created_at).toLocaleDateString()}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.text.secondary }}>
                                        <TimeIcon fontSize="small" />
                                        <Typography variant="body2">{calculateReadTime(blog.content)}</Typography>
                                    </Box>
                                </Box>

                                <Card sx={{ mb: 4, borderRadius: '16px', overflow: 'hidden' }}>
                                    <CardMedia
                                        component="img"
                                        image={blog.featured_image}
                                        alt={blog.title}
                                        sx={{ width: '100%', height: 'auto', maxHeight: '500px', objectFit: 'cover' }}
                                    />
                                </Card>

                                <Box
                                    sx={{
                                        color: theme.palette.text.primary,
                                        '& p': { fontSize: '1.1rem', lineHeight: 1.8, mb: 3 },
                                        '& h2': { color: theme.palette.heading.h2, fontSize: '1.75rem', fontWeight: 600, mt: 5, mb: 2 },
                                        '& ul': { pl: 3, mb: 3 },
                                        '& li': { mb: 1, fontSize: '1.1rem', lineHeight: 1.8 },
                                        '& strong': { fontWeight: 'bold' }
                                    }}
                                    dangerouslySetInnerHTML={{ __html: blog.content }}
                                />
                            </Box>
                        </Grid>

                        {/* Sidebar for Related Posts */}
                        <Grid item xs={12} md={4}>
                            <Box sx={{ position: 'sticky', top: '100px' }}>
                                {relatedPosts?.length > 0 &&
                                    <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                                        Related Articles
                                    </Typography>
                                }
                                <Grid container spacing={3}>
                                    {relatedPosts.map(post => (
                                        <Grid item xs={12} key={post.id}>
                                            <Card sx={{ display: 'flex' }}>
                                                <CardMedia
                                                    component="img"
                                                    sx={{ width: 120, objectFit: 'cover' }}
                                                    image={post.featured_image}
                                                    alt={post.title}
                                                />
                                                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                                    <CardContent sx={{ flex: '1 0 auto', p: 2 }}>
                                                        <LinkMUI component={Link} to={`/blog/${post.slug}`} underline="hover" color="text.primary">
                                                            <Typography component="div" variant="body1" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
                                                                {post.title}
                                                            </Typography>
                                                        </LinkMUI>
                                                        <Typography variant="caption" color="text.secondary" component="div">
                                                            {new Date(post.created_at).toLocaleDateString()}
                                                        </Typography>
                                                    </CardContent>
                                                </Box>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </ThemeProvider>
    );
};

export default BlogDetail;
